<template>

    <div class="page">

        <!-- KtDatatable Implementation -->
        <kt-datatable
            :model="partModel"
            :data="rows"
            :filter="datatableFilter"
            :meta="{ tableClass: '-table-bordered', rowClass: getRowClass }"
            :update-function="handleDatatableUpdate"
            :hide-search="false"
            storage-key="part-list"
            :request-id="props.requestId"
        >
            <template #header_select="scope">
                <div class="form-check">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        :checked="selectAll"
                        @change="toggleSelectAll"
                        @click.stop
                        title="Select all on this page"
                    />
                </div>
            </template>
            
            <template #view_select="scope">
                <div class="form-check">
                    <input 
                        class="form-check-input" 
                        type="checkbox" 
                        :checked="selectedRows.includes(scope.row.Id)"
                        @change="toggleRowSelection(scope.row.Id)"
                        @click.stop
                    />
                </div>
            </template>
            
            <template #filters="scope">
                <div class="col" v-if="!props.hideFilters">
                    <div class="row">
                        <div class="col-auto">
                            <client-multi-check 
                                label="Clients"
                                :model-value="currentFilter.client"
                                @update:model-value="(value) => setFilter({client: value})"
                            ></client-multi-check>
                        </div>
                        <div class="col-auto">
                            <tech-multi-check 
                                label="Technicians"
                                :model-value="currentFilter.technician"
                                @update:model-value="(value) => setFilter({technician: value})"
                            ></tech-multi-check>
                        </div>
                        <div class="col-auto">
                            <order-status-multi-check 
                                label="Order Status"
                                :model-value="currentFilter.orderStatus"
                                @update:model-value="(value) => setFilter({orderStatus: value})"
                            ></order-status-multi-check>
                        </div>
                        <div class="col-auto">
                            <kt-date-range-select
                                label="Date Range"
                                v-model="dateRange"
                                @change="handleDateRangeChange"
                            ></kt-date-range-select>
                            
                        </div>
                    </div>
                </div>
            </template>

            <template #view_actions="scope">
                <kt-dropmenu v-model:visible="actionDropdown['row_' + scope.row.Id]">
                    <template #button>
                        <button
                            class="btn -btn-outline-primary btn-sm"
                            @click="actionDropdown['row_' + scope.row.Id] = !actionDropdown['row_' + scope.row.Id]"
                        >
                            <span><i class="fas fa-ellipsis-v"></i></span>
                        </button>
                    </template>
                    <template #dropmenu>
                        <ul class="dropdown-menu show">
                            <li>
                                <label class="dropdown-item">
                                    <button class="btn btn-sm btn-outline-primary form-control" @click="editRecord(scope.row.Id)">
                                        <i class="fa fa-edit"></i> Edit
                                    </button>
                                </label>
                            </li>
                            <li>
                                <label class="dropdown-item">
                                    <button class="btn btn-sm btn-outline-danger form-control" @click="deleteRecord(scope.row.Id)">
                                        <i class="fa fa-trash"></i> Remove
                                    </button>
                                </label>
                            </li>
                        </ul>
                    </template>
                </kt-dropmenu>
            </template>

            <template #view_string="scope">
                <span v-if="scope.column.name === 'Contact'">
                    {{ scope.row.Contact?.Company || 'N/A' }}
                </span>
                <span v-else-if="scope.column.name === 'RequestId'">
                    {{ scope.row.Request?.Id || 'N/A' }}
                </span>
                <span v-else-if="scope.column.name === 'TechnicianName'">
                    {{ scope.row.Technician?.TechnicianName || 'N/A' }}
                </span>
                <span v-else>
                    {{ scope.value }}
                </span>
            </template>

            <template #view_date="scope">
                <span>
                    {{ getDate(scope.value) }}
                </span>
            </template>

            <template #view_number="scope">
                <span v-if="scope.column.name === 'PartCost'">
                    ${{ (scope.value || 0).toFixed(2) }}
                </span>
                <span v-else>
                    {{ scope.value }}
                </span>
            </template>

            <template #view_custom="scope">
                <span v-if="scope.column.name === 'OrderStatus'">
                    <i :class="getOrderStatusIcon(scope.value)" class="me-1"></i>
                    {{ getOrderStatusName(scope.value) }}
                </span>
                <span v-else>
                    {{ scope.value }}
                </span>
            </template>

            <template #after-search="scope">
                <div class="btn-group">
                <button @click="refresh()" 
                    class="btn btn-sm btn-outline-secondary"
                >Refresh <i class="fa fa-sync"></i></button>

                <button 
                    v-if="!props.disableActions"
                    @click="newRecord()" 
                    class="btn btn-sm btn-outline-primary"
                >New <i class="fa fa-plus"></i></button>
                </div>
            </template>

            <!-- Filter badges in under-header slot -->
            <template #under-header>
                <!-- Bulk Actions Toolbar -->
                <div v-if="selectedRows.length > 0" class="mb-0 p-2 actions-row">
                    <div class="d-flex align-items-center gap-2">
                        <span class="fw-bold text-primary">{{ selectedRows.length }} selected</span>
                        <button 
                            class="btn btn-sm btn-outline-danger"
                            @click="showDeleteModal = true"
                            title="Delete Selected"
                        >
                            <i class="fas fa-trash"></i> Delete
                        </button>
                        <button 
                            class="btn btn-sm btn-outline-secondary me-auto"
                            @click="clearSelection"
                        >
                            Clear Selection
                        </button>
                    </div>
                </div>
                
                <div v-if="hasActiveFilters" class="d-flex flex-wrap gap-2 p-2">
                    <span class="text-muted small me-2">Active filters:</span>
                    
                    <!-- Client badges -->
                    <span v-for="clientId in currentFilter.client" :key="'client-' + clientId" 
                          class="badge bg-primary d-flex align-items-center">
                      Client: {{ getClientName(clientId) }}
                      <button type="button" class="btn-close btn-close-white ms-2" 
                              @click="removeClientFilter(clientId)"
                              style="font-size: 0.7em;"></button>
                    </span>
                    
                    <!-- Technician badges -->
                    <span v-for="techId in currentFilter.technician" :key="'tech-' + techId"
                          class="badge bg-success d-flex align-items-center">
                      Technician: {{ getTechnicianName(techId) }}
                      <button type="button" class="btn-close btn-close-white ms-2" 
                              @click="removeTechnicianFilter(techId)"
                              style="font-size: 0.7em;"></button>
                    </span>
                    
                    <!-- Order Status badges -->
                    <span v-for="statusId in currentFilter.orderStatus" :key="'status-' + statusId"
                          class="badge bg-warning d-flex align-items-center">
                      Status: {{ getOrderStatusName(statusId) }}
                      <button type="button" class="btn-close btn-close-white ms-2" 
                              @click="removeOrderStatusFilter(statusId)"
                              style="font-size: 0.7em;"></button>
                    </span>
                    
                    <!-- Date range badge -->
                    <span v-if="currentFilter.startDate || currentFilter.endDate"
                          class="badge bg-info d-flex align-items-center">
                      Date: {{ formatDateRange() }}
                      <button type="button" class="btn-close btn-close-white ms-2" 
                              @click="removeDateFilter()"
                              style="font-size: 0.7em;"></button>
                    </span>
                    
                    <!-- Clear all filters -->
                    <button v-if="hasActiveFilters" 
                            class="btn btn-sm btn-outline-secondary ms-2" 
                            @click="clearAllFilters()">
                      Clear All
                    </button>
                </div>
            </template>
        </kt-datatable>
        
        <!-- Bulk Delete Confirmation Modal -->
        <kt-modal
            :visible="showDeleteModal"
            title="Confirm Bulk Delete"
            @dismiss="showDeleteModal = false"
        >
            <template #body>
                <div class="container-fluid">
                    <p class="text-danger mb-3">
                        <i class="fas fa-exclamation-triangle"></i>
                        Are you sure you want to delete {{ selectedRows.length }} part(s)? This action cannot be undone.
                    </p>
                </div>
            </template>
            <template #footer>
                <button class="btn btn-secondary" @click="showDeleteModal = false">Cancel</button>
                <button class="btn btn-danger" @click="confirmBulkDelete">Delete</button>
            </template>
        </kt-modal>
        
        <kt-modal
            :visible="filterVisible"
            :title="filterTitle"
            @dismiss="filterVisible = 0"
        >
            <template #body>
                <part-filter 
                    :filter="filter"
                    @dismiss="filterVisible = 0" 
                    @update="setFilter($event)"
                ></part-filter>
            </template>
        </kt-modal>
        
        <router-view name="modal"></router-view>
        
        <!-- Embedded Edit Modal -->
        <part-edit-embedded 
            v-if="props.embedded && showEditModal" 
            :record-id="editingRecordId"
            :request-id="props.requestId"
            :client-id="props.clientId"
            :tech-id="props.techId"
            :pm-only="props.pmOnly"
            @close="closeEditModal"
        />
        
    </div>
    
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed } from "vue";
import { useRouter, useRoute, RouterView } from 'vue-router';
import Api, { ApiResponse } from "@/services/ApiService";
import type { PartModel } from "@/models/PartModel";
import Helper from "@/services/Helper";
import Cache from "@/services/Cache";
import KtModal from '@/components/UI/KtModal.vue';
import KtDatatable from "@/components/UI/KtDatatable.vue";
import KtDropmenu from "@/components/UI/KtDropmenu.vue";
import { KtDatatableModelProp, KtDatatableData } from "@/components/UI/KtDataTable";

import { getActivePinia, storeToRefs } from "pinia";

// stores ...
import { useClientStore } from '@/stores/ClientStore';
import { useTechStore } from "@/stores/TechStore";
import { useOrderStatusStore } from "@/stores/OrderStatusStore";
import ClientMultiCheck from "@/components/Clients/ClientMultiCheck.vue";
import TechMultiCheck from "@/components/Techs/TechMultiCheck.vue";
import OrderStatusMultiCheck from "@/components/Parts/OrderStatusMultiCheck.vue";
import KtDateRangeSelect from "@/components/UI/KtDateRangeSelect.vue";
import PartEditEmbedded from "@/components/Parts/PartEditEmbedded.vue";

const techStore = useTechStore();
const { list: TechList } = storeToRefs(techStore);

const clientStore = useClientStore();
const { list: ClientList } = storeToRefs(clientStore);

const orderStatusStore = useOrderStatusStore();
const { list: OrderStatusList } = storeToRefs(orderStatusStore);

// reload stores for now ... can this be moved into the useStore methods ?
techStore.reload();
clientStore.reload();
orderStatusStore.reload();

const router = useRouter();
const route = useRoute();

// Props
const props = defineProps({
    requestId: {
        type: [Number, String],
        default: null
    },
    clientId: {
        type: [Number, String],
        default: null
    },
    techId: {
        type: [Number, String],
        default: null
    },
    hideFilters: {
        type: Boolean,
        default: false
    },
    embedded: {
        type: Boolean,
        default: false
    },
    pmOnly: {
        type: Boolean,
        default: false
    },
    disableActions: {
        type: Boolean,
        default: false
    }
});

const total = ref(0);
const rows = ref<any[]>([]);

// Selection state
const selectedRows = ref<number[]>([]);
const selectAll = ref(false);
const showDeleteModal = ref(false);

// ===== NEW CLEAN FILTER SYSTEM =====

// Filter state interface
interface PartListFilter {
    client: number[];
    technician: number[];
    orderStatus: number[];
    user: string;
    searchTerm: string;
    startDate: string;
    endDate: string;
    page: number;
    size: number;
    sort: Array<Record<string, 'ASC' | 'DESC'>>;
}

// Current filter state (reactive)
const currentFilter = ref<PartListFilter>({
    client: [],
    technician: [],
    orderStatus: [],
    user: '',
    searchTerm: '',
    startDate: '',
    endDate: '',
    page: 0,
    size: 25,
    sort: []
});

// Datatable filter for pagination (derived from currentFilter)
const datatableFilter = computed(() => ({
    page: currentFilter.value.page,
    size: currentFilter.value.size,
    total: total.value,
    search: currentFilter.value.searchTerm,
    sort: currentFilter.value.sort
}));

// Date range picker (derived from currentFilter)
const dateRange = computed({
    get: () => ({
        startDate: currentFilter.value.startDate || null,
        endDate: currentFilter.value.endDate || null
    }),
    set: (value) => {
        // Use setFilter to maintain consistency with other components
        setFilter({
            startDate: value.startDate || '',
            endDate: value.endDate || ''
        });
    }
});

const actionDropdown = ref<Record<string, boolean>>({});

// Embedded mode state
const showEditModal = ref(false);
const editingRecordId = ref<string | null>(null);

// Legacy variables for existing filter components (will be updated later)
const filterVisible = ref(false);
const filterTitle = ref("Filter Part Entries");
const filter = computed(() => ({
    client: currentFilter.value.client.map(c => c.toString()).join(','),
    technician: currentFilter.value.technician.map(t => t.toString()).join(','),
    user: currentFilter.value.user,
    searchTerm: currentFilter.value.searchTerm,
    startDate: currentFilter.value.startDate,
    endDate: currentFilter.value.endDate,
    page: currentFilter.value.page.toString()
}));

// ===== URL & FILTER UTILITIES =====

// Parse sort parameter from URL string to KtDatatable sort array format  
const parseSortFromUrl = (sortParam?: string): Array<Record<string, 'ASC' | 'DESC'>> => {
    if (!sortParam) return [];
    
    // Parse comma-separated sort string like "Id,-CreateDate,PartCost"
    // Where "-" prefix means DESC, no prefix means ASC
    return sortParam.split(',').map(sortField => {
        const trimmed = sortField.trim();
        if (trimmed.startsWith('-')) {
            const column = trimmed.substring(1);
            return { [column]: 'DESC' as const };
        } else {
            return { [trimmed]: 'ASC' as const };
        }
    });
};

// Parse URL params to filter object
const parseUrlToFilter = (): PartListFilter => {
    const routeQuery = route.query;
    
    console.debug('Parsing URL params from route.query:', routeQuery);
    console.debug('Raw page param:', routeQuery.page);
    
    const parsed = {
        client: Array.isArray(routeQuery.client) 
            ? routeQuery.client.filter(c => c !== null).map(c => parseInt(c as string))
            : (routeQuery.client ? [parseInt(routeQuery.client as string)] : []),
        technician: Array.isArray(routeQuery.technician) 
            ? routeQuery.technician.filter(t => t !== null).map(t => parseInt(t as string))
            : (routeQuery.technician ? [parseInt(routeQuery.technician as string)] : []),
        orderStatus: Array.isArray(routeQuery.orderStatus) 
            ? routeQuery.orderStatus.filter(s => s !== null).map(s => parseInt(s as string))
            : (routeQuery.orderStatus ? [parseInt(routeQuery.orderStatus as string)] : []),
        user: (routeQuery.user as string) || '',
        searchTerm: (routeQuery.searchTerm as string) || '',
        startDate: (routeQuery.startDate as string) || '',
        endDate: (routeQuery.endDate as string) || '',
        page: parseInt((routeQuery.page as string) || '0'),
        size: parseInt((routeQuery.size as string) || '25'),
        sort: parseSortFromUrl(routeQuery.sort as string)
    };
    
    console.debug('Parsed filter:', parsed);
    return parsed;
};

// Build URL params from filter object
const buildUrlFromFilter = (filter: PartListFilter): string => {
    const params = new URLSearchParams();
    
    // Add array parameters
    filter.client.forEach(c => c && params.append('client', c.toString()));
    filter.technician.forEach(t => t && params.append('technician', t.toString()));
    filter.orderStatus.forEach(s => s && params.append('orderStatus', s.toString()));
    
    // Add single parameters
    if (filter.user) params.set('user', filter.user);
    if (filter.searchTerm) params.set('searchTerm', filter.searchTerm);
    if (filter.startDate) params.set('startDate', filter.startDate);
    if (filter.endDate) params.set('endDate', filter.endDate);
    
    // Add sort parameter - convert KtDatatable sort array back to URL string
    if (filter.sort && filter.sort.length > 0) {
        const sortString = filter.sort.map(sortObj => {
            const [column, direction] = Object.entries(sortObj)[0];
            return direction === 'DESC' ? `-${column}` : column;
        }).join(',');
        params.set('sort', sortString);
    }
    
    // Always include page parameter (even for page 0/1)
    params.set('page', filter.page.toString());
    if (filter.size !== 25) params.set('size', filter.size.toString());
    
    return params.toString();
};

// Save filter to Cache service for persistence (consistent with JobList)
const saveFilterToStorage = (filter: PartListFilter) => {
    try {
        Cache.set('part-list-filter', filter);
    } catch (e) {
        console.warn('Failed to save filter to Cache:', e);
    }
};

// Load filter from Cache service as fallback (consistent with JobList)
const loadFilterFromStorage = (): PartListFilter => {
    try {
        const stored = Cache.get('part-list-filter');
        if (stored) {
            return { ...parseUrlToFilter(), ...stored };
        }
    } catch (e) {
        console.warn('Failed to load filter from Cache:', e);
    }
    return parseUrlToFilter();
};

// ===== API INTEGRATION =====

// Convert filter to API parameters
const buildApiParams = (filter: PartListFilter) => {
    const params: any = {
        Page: filter.page,
        Size: filter.size,
        IncludeRequest: true,
        IncludeContact: true
    };
    
    // If requestId prop is provided, filter by that request only
    if (props.requestId) {
        params.RequestId = props.requestId;
        // Don't apply other filters when filtering by request
        return params;
    }
    
    // Add array filters
    if (filter.client.length > 0) params.CustomerNumber = filter.client;
    if (filter.technician.length > 0) params.TechId = filter.technician;
    if (filter.orderStatus.length > 0) params.OrderStatus = filter.orderStatus;
    
    // Add single filters
    if (filter.user) params.Owner = filter.user;
    if (filter.searchTerm) params.SearchTerm = filter.searchTerm;
    if (filter.startDate) params.StartDate = filter.startDate;
    if (filter.endDate) params.EndDate = filter.endDate;
    
    // Add OrderBy parameter for server-side sorting
    if (filter.sort && filter.sort.length > 0) {
        // Convert sort array to comma-separated OrderBy string
        // Example: [{Id: 'ASC'}] -> "Id", [{Id: 'DESC'}] -> "-Id"
        const orderByParts = filter.sort.map(sortObj => {
            const [column, direction] = Object.entries(sortObj)[0];
            return direction === 'DESC' ? `-${column}` : column;
        });
        params.OrderBy = orderByParts.join(',');
    }
    
    return params;
};

// Load data from API using current filter (matching JobList signature)
const loadData = async () => {
    console.debug('loadData called with currentFilter:', currentFilter.value);
    
    const params = buildApiParams(currentFilter.value);
    console.debug('API params:', params);
    
    try {
        const response: ApiResponse<PartModel[]> = await Api.loadParts(params);
        console.debug('API response:', response);
        
        // Handle ServerResponse format like JobList
        rows.value = response.Data || [];
        
        // Update pagination metadata
        if (response.Meta && response.Meta.Total) {
            total.value = response.Meta.Total;
        } else {
            // Fallback for legacy response format
            total.value = rows.value.length;
        }
        
        console.debug('Updated parts:', rows.value.length, 'Total:', total.value);
    } catch (error) {
        console.error('Failed to load parts:', error);
        rows.value = [];
        total.value = 0;
    }
};

// ===== FILTER BADGE FUNCTIONS =====

// Computed property to check if any filters are active
const hasActiveFilters = computed(() => {
    return currentFilter.value.client.length > 0 ||
           currentFilter.value.technician.length > 0 ||
           currentFilter.value.orderStatus.length > 0 ||
           currentFilter.value.searchTerm ||
           currentFilter.value.startDate ||
           currentFilter.value.endDate;
});

// Helper functions to get names for display
const getClientName = (customerNumber: number) => {
    // Find client by CustomerNumber since ClientList is an array
    const client = ClientList.value.find((c: any) => c.CustomerNumber === customerNumber);
    return client?.Company || `Client #${customerNumber}`;
};

const getTechnicianName = (techId: number) => {
    // TechList is a Record/dictionary, access by key
    const tech = TechList.value[techId];
    return tech?.TechnicianName || `Tech #${techId}`;
};

const getOrderStatusName = (statusId: number) => {
    // OrderStatusList is a Record/dictionary, access by key
    const status = OrderStatusList.value[statusId];
    return status?.Description || `Missing Status ${statusId}`;
};

// Helper function to get Font Awesome icon based on order status
const getOrderStatusIcon = (statusId: number) => {
    const status = OrderStatusList.value[statusId];
    const statusName = status?.Description?.toLowerCase() || '';
    
    if (statusName.includes('received') || statusName.includes('delivered') || statusName.includes('complete')) {
        return 'fas fa-check-circle';
    } else if (statusName.includes('pending') || statusName.includes('waiting') || statusName.includes('ordered')) {
        return 'fas fa-clock';
    } else if (statusName.includes('shipping') || statusName.includes('transit') || statusName.includes('progress')) {
        return 'fas fa-truck';
    } else if (statusName.includes('cancelled') || statusName.includes('rejected') || statusName.includes('failed')) {
        return 'fas fa-times-circle';
    } else if (statusName.includes('back') || statusName.includes('backorder')) {
        return 'fas fa-exclamation-triangle';
    } else {
        return 'fas fa-circle';
    }
};

// Helper function to get row class based on order status
const getRowClass = (row: any) => {
    const statusId = row.OrderStatus;
    if (!statusId) return '';
    
    const status = OrderStatusList.value[statusId];
    const statusName = status?.Description?.toLowerCase() || '';
    
    if (statusName.includes('received') || statusName.includes('delivered') || statusName.includes('complete')) {
        return 'row-status-completed';
    } else if (statusName.includes('pending') || statusName.includes('waiting') || statusName.includes('ordered')) {
        return 'row-status-pending';
    } else if (statusName.includes('shipping') || statusName.includes('transit') || statusName.includes('progress')) {
        return 'row-status-progress';
    } else if (statusName.includes('cancelled') || statusName.includes('rejected') || statusName.includes('failed')) {
        return 'row-status-cancelled';
    } else if (statusName.includes('back') || statusName.includes('backorder')) {
        return 'row-status-scheduled';
    }
    return '';
};

const formatDateRange = () => {
    const start = currentFilter.value.startDate;
    const end = currentFilter.value.endDate;
    if (start && end) {
        return `${start} to ${end}`;
    } else if (start) {
        return `From ${start}`;
    } else if (end) {
        return `Until ${end}`;
    }
    return '';
};

// Filter removal functions
const removeClientFilter = (clientId: number) => {
    const newClients = currentFilter.value.client.filter(id => id !== clientId);
    setFilter({ client: newClients });
};

const removeTechnicianFilter = (techId: number) => {
    const newTechs = currentFilter.value.technician.filter(id => id !== techId);
    setFilter({ technician: newTechs });
};

const removeOrderStatusFilter = (statusId: number) => {
    const newStatuses = currentFilter.value.orderStatus.filter(id => id !== statusId);
    setFilter({ orderStatus: newStatuses });
};

const removeDateFilter = () => {
    setFilter({ startDate: '', endDate: '' });
};

const clearAllFilters = () => {
    setFilter({
        client: [],
        technician: [],
        orderStatus: [],
        searchTerm: '',
        startDate: '',
        endDate: '',
        sort: []
    });
};

// ===== LEGACY FUNCTION REPLACEMENTS =====

const editRecord = (id: any) => {
    if (props.embedded) {
        editingRecordId.value = id.toString();
        showEditModal.value = true;
    } else {
        router.push({
            path: "/part/edit/" + id
        });
    }
};

const newRecord = () => {
    if (props.embedded) {
        editingRecordId.value = 'new';
        showEditModal.value = true;
    } else {
        router.push({
            path: "/part/new"
        });
    }
};

const closeEditModal = () => {
    showEditModal.value = false;
    editingRecordId.value = null;
    // Refresh the list after closing modal
    refresh();
};

const refresh = () => {
    loadData();
};

const deleteRecord = (id: any) => {
    if (window.confirm("Are you sure you want to delete this record?")) {
        //parent.$refs.filter.deleteRecord(id);
    }
};

const getDate = (str: any) => {
    return Helper.dateToInput(new Date(str), Helper.DateFormat_Date);
};

// Updated setFilter for new system - builds URL and navigates (like pagination)
const setFilter = (filterData: any) => {
    console.debug('setFilter (new system):', filterData);
    
    // If embedded mode (requestId prop), ignore filter updates
    if (props.requestId) {
        console.debug('Embedded mode - ignoring filter updates');
        return;
    }
    
    // Merge with current filter
    const newFilter = { ...currentFilter.value, ...filterData };
    
    // Reset to page 0 when filter changes (except when only page changes)
    if (!filterData.page && Object.keys(filterData).some(key => key !== 'page')) {
        newFilter.page = 0;
    }
    
    // Build URL and navigate directly (like JobList)
    const urlParams = buildUrlFromFilter(newFilter);
    const newUrl = `/part/list${urlParams ? '?' + urlParams : ''}`;
    
    console.debug('setFilter navigating to:', newUrl);
    router.replace(newUrl);
};

// Pagination and sorting handler for datatable
const handleDatatableUpdate = (datatableFilter: any) => {
    console.debug('Datatable update (pagination/sorting/search):', datatableFilter);
    
    // If embedded mode (requestId prop), update filter directly without routing
    if (props.requestId) {
        if (datatableFilter.page !== undefined) currentFilter.value.page = datatableFilter.page;
        if (datatableFilter.size !== undefined) currentFilter.value.size = datatableFilter.size;
        if (datatableFilter.sort !== undefined) currentFilter.value.sort = datatableFilter.sort || [];
        if (datatableFilter.search !== undefined) currentFilter.value.searchTerm = datatableFilter.search;
        loadData();
        return;
    }
    
    setFilter({
        page: datatableFilter.page,
        size: datatableFilter.size,
        sort: datatableFilter.sort || [],
        searchTerm: datatableFilter.search || ''
    });
};

// KtDatatable model definition for Part list
const partModel = [
    {
        name: "Select",
        label: "Select",
        type: "select",
        width: 60,
        locked: true,
        draggable: false
    },
    {
        name: "Actions",
        label: "Actions",
        type: "actions",
        display: true
    },
    {
        name: "Id",
        label: "ID",
        display: true,
        sortable: true
    },
    {
        name: "Contact",
        label: "Client",
        display: true,
        type: "string"
    },
    {
        name: "RequestId",
        label: "Request ID",
        display: true,
        type: "string"
    },
    {
        name: "PartDescription",
        label: "Description",
        display: true,
        type: "string"
    },
    {
        name: "PartNumber",
        label: "Part Number",
        display: true,
        type: "string"
    },
    {
        name: "PartsCost",
        label: "Cost",
        display: true,
        type: "number",
        sortable: true
    },
    {
        name: "Quantity",
        label: "Quantity",
        display: true,
        type: "number",
        sortable: true
    },
    {
        name: "TechnicianName",
        label: "Technician",
        display: true,
        type: "string"
    },
    {
        name: "CreateDate",
        label: "Created",
        display: true,
        type: "date",
        sortable: true
    },
    {
        name: "NeededBy",
        label: "Needed By",
        display: true,
        type: "date",
        sortable: true
    },
    {
        name: "WorkCompleteDate",
        label: "Completed",
        display: true,
        type: "date",
        sortable: true
    },
    {
        name: "OrderStatus",
        label: "Status",
        display: true,
        type: "custom"
    }
] as Array<KtDatatableModelProp>;

// ===== INITIALIZATION & ROUTE WATCHING =====

// Initialize filters and load data when route changes
const initializeFromRoute = () => {
    console.debug('Route changed, initializing filter from route query:', route.query);
    
    const newFilter = parseUrlToFilter();
    currentFilter.value = newFilter;
    
    // Save to localStorage for persistence
    saveFilterToStorage(newFilter);
    
    // Load data with updated currentFilter
    loadData();
};

// Watch route changes to update filter (matching JobList pattern)
watch(() => route.query, (newQuery) => {
    // Skip route watching in embedded mode
    if (props.requestId) return;
    
    console.debug('=== ROUTE WATCHER TRIGGERED ===');
    console.debug('Route query changed:', newQuery);
    console.debug('Current URL:', window.location.href);
    
    // Parse new filter from URL
    const newFilter = parseUrlToFilter();
    
    // Only update if filter actually changed (avoid infinite loops)
    const currentParams = buildUrlFromFilter(currentFilter.value);
    const newParams = buildUrlFromFilter(newFilter);
    
    console.debug('Current params:', currentParams);
    console.debug('New params:', newParams);
    console.debug('Params equal?', currentParams === newParams);
    console.debug('Current filter page:', currentFilter.value.page);
    console.debug('New filter page:', newFilter.page);
    
    if (currentParams !== newParams) {
        console.debug('✅ Filter changed, updating currentFilter and calling loadData()');
        currentFilter.value = newFilter;
        
        // Update cache with new filter state (including when cleared)
        saveFilterToStorage(newFilter);
        
        loadData();
    } else {
        console.debug('❌ Filter unchanged, skipping update');
    }
}, { immediate: false });

// ===== SELECTION MANAGEMENT =====

const toggleRowSelection = (rowId: number) => {
    const index = selectedRows.value.indexOf(rowId);
    if (index > -1) {
        selectedRows.value.splice(index, 1);
    } else {
        selectedRows.value.push(rowId);
    }
    updateSelectAllState();
};

const toggleSelectAll = () => {
    if (selectAll.value) {
        selectedRows.value = [];
        selectAll.value = false;
    } else {
        selectedRows.value = rows.value.map((row: any) => row.Id);
        selectAll.value = true;
    }
};

const updateSelectAllState = () => {
    if (rows.value.length === 0) {
        selectAll.value = false;
        return;
    }
    const allSelected = rows.value.every((row: any) => selectedRows.value.includes(row.Id));
    selectAll.value = allSelected;
};

const clearSelection = () => {
    selectedRows.value = [];
    selectAll.value = false;
};

// ===== BULK ACTIONS =====

const confirmBulkDelete = async () => {
    if (selectedRows.value.length === 0) return;
    
    console.warn(`Bulk delete for ${selectedRows.value.length} part(s) - API implementation needed`);
    // TODO: Implement when API is available
    // try {
    //   for (const id of selectedRows.value) {
    //     await Api.deletePart(id);
    //   }
    //   Alert.notify({ text: `Successfully deleted ${selectedRows.value.length} part(s)`, type: 'success' });
    //   clearSelection();
    //   await refresh();
    // } catch (error) {
    //   console.error('Error deleting parts:', error);
    //   Alert.notify({ text: 'Failed to delete some parts', type: 'error' });
    // }
    
    showDeleteModal.value = false;
};

// Initialize filter from URL or localStorage (matching JobList pattern)
const initializeFilter = () => {
    // If embedded mode (requestId prop), skip URL initialization
    if (props.requestId) {
        console.debug('Embedded mode - using default filter');
        return;
    }
    
    // Priority: URL params > localStorage > defaults
    const urlFilter = parseUrlToFilter();
    const hasUrlParams = window.location.search.length > 0;
    
    if (hasUrlParams) {
        // URL has params, use them
        currentFilter.value = urlFilter;
    } else {
        // No URL params, try localStorage
        currentFilter.value = loadFilterFromStorage();
    }
    
    console.debug('Initialized filter:', currentFilter.value);
};

// Mount and initialize (matching JobList pattern)
onMounted(() => {
    console.debug('PartList mounted, initializing...');
    initializeFilter();
    loadData();
});

</script>

<style scoped>
/* Row shading based on status - applied to td cells */
:deep(.row-status-completed td) {
    background-color: rgba(25, 135, 84, 0.08) !important;
}

:deep(.row-status-completed:hover td) {
    background-color: rgba(25, 135, 84, 0.15) !important;
}

:deep(.row-status-pending td) {
    background-color: rgba(255, 193, 7, 0.08) !important;
}

:deep(.row-status-pending:hover td) {
    background-color: rgba(255, 193, 7, 0.15) !important;
}

:deep(.row-status-progress td) {
    background-color: rgba(13, 110, 253, 0.08) !important;
}

:deep(.row-status-progress:hover td) {
    background-color: rgba(13, 110, 253, 0.15) !important;
}

:deep(.row-status-cancelled td) {
    background-color: rgba(220, 53, 69, 0.08) !important;
}

:deep(.row-status-cancelled:hover td) {
    background-color: rgba(220, 53, 69, 0.15) !important;
}

:deep(.row-status-scheduled td) {
    background-color: rgba(13, 202, 240, 0.08) !important;
}

:deep(.row-status-scheduled:hover td) {
    background-color: rgba(13, 202, 240, 0.15) !important;
}

:deep(.row-status-draft td) {
    background-color: rgba(108, 117, 125, 0.08) !important;
}

:deep(.row-status-draft:hover td) {
    background-color: rgba(108, 117, 125, 0.15) !important;
}
</style>